import React, { JSX } from 'react';

import { HomeFilterData } from '@/lib/validations/HomeFilterSchema';

interface State {
  filtersData: HomeFilterData;
  filtersAppliedCount: number;
}

interface SetFiltersData {
  type: 'SET_FILTERS_DATA';
  filtersData: HomeFilterData;
}

interface ResetFiltersData {
  type: 'RESET_FILTERS_DATA';
}

interface IncrementApplyCount {
  type: 'INCREMENT_APPLY_COUNT';
}

type Action = ResetFiltersData | SetFiltersData | IncrementApplyCount;

type Dispatch = (action: Action) => void;

interface FiltersContextData {
  state: State;
  dispatch: Dispatch;
  setFilters: (filtersData: HomeFilterData) => void;
  resetFilters: () => void;
  incrementApplyCount: () => void;
}

const defaultValues: HomeFilterData = {
  sort_light_rating: 'htl',
  denominations: [],
  programs: [],
  distance: 150
  // rating: 4
};

const FiltersContext = React.createContext<FiltersContextData | undefined>(undefined);

function filtersReducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_FILTERS_DATA':
      return {
        ...state,
        filtersData: action.filtersData
      };
    case 'RESET_FILTERS_DATA':
      return {
        ...state,
        filtersData: defaultValues
      };

    case 'INCREMENT_APPLY_COUNT':
      return {
        ...state,
        filtersAppliedCount: state.filtersAppliedCount + 1
      };

    default:
      throw new Error(`Unhandled action type`);
  }
}

function FiltersProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [state, dispatch] = React.useReducer(filtersReducer, { filtersData: defaultValues, filtersAppliedCount: 0 });

  const setFilters = (filtersData: HomeFilterData) => {
    dispatch({
      type: 'SET_FILTERS_DATA',
      filtersData: filtersData
    });
  };

  const resetFilters = () => {
    dispatch({
      type: 'RESET_FILTERS_DATA'
    });
  };

  const incrementApplyCount = () => {
    dispatch({
      type: 'INCREMENT_APPLY_COUNT'
    });
  };

  return (
    <FiltersContext.Provider value={{ state, dispatch, setFilters, resetFilters, incrementApplyCount }}>
      {children}
    </FiltersContext.Provider>
  );
}

function useFiltersData(): FiltersContextData {
  const context = React.useContext(FiltersContext);
  if (context === undefined) {
    throw new Error('useFiltersData must be used within a FiltersProvider');
  }
  return context;
}

export { FiltersProvider, useFiltersData };
